<template>
  <div class="flex flex-col gap-6 p-6">
    <div class="bg-white rounded-lg shadow p-6">
      <h1 class="text-2xl font-bold text-gray-900 mb-2">Campaign Reports</h1>
      <p class="text-gray-600">Track the performance of your message campaigns</p>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex flex-wrap gap-4 items-end">
        <!-- Date Range Filter -->
        <div class="flex-1 min-w-[200px]">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Date Range
          </label>
          <select
            v-model="selectedDateRange"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            @change="onDateRangeChange"
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 3 Months</option>
            <option value="180">Last 6 Months</option>
            <option value="365">Last Year</option>
          </select>
        </div>

        <!-- Label Filter -->
        <div class="flex-1 min-w-[200px]">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Label
          </label>
          <select
            v-model="selectedLabel"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            @change="onLabelChange"
          >
            <option value="">All Labels</option>
            <option
              v-for="label in labels"
              :key="label.id"
              :value="label.id"
            >
              {{ label.title }}
            </option>
          </select>
        </div>

        <!-- Apply Button -->
        <div>
          <button
            class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            @click="fetchData"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
    
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div class="bg-white rounded-lg shadow p-6">
        <div class="text-sm font-medium text-gray-500">Messages Sent</div>
        <div class="text-2xl font-bold text-blue-600">{{ campaignSummary.sent_count || 0 }}</div>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <div class="text-sm font-medium text-gray-500">Messages Delivered</div>
        <div class="text-2xl font-bold text-gray-900">{{ campaignSummary.delivered_count || 0 }}</div>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <div class="text-sm font-medium text-gray-500">Messages Read</div>
        <div class="text-2xl font-bold text-purple-600">{{ campaignSummary.read_count || 0 }}</div>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <div class="text-sm font-medium text-gray-500">Messages Failed</div>
        <div class="text-2xl font-bold text-red-600">{{ campaignSummary.failed_count || 0 }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'CampaignReports',
  data() {
    return {
      selectedDateRange: '30', // Default to 30 days
      selectedLabel: '', // Default to all labels
    };
  },
  computed: {
    ...mapGetters({
      campaignMetrics: 'campaignReports/getAccountReports',
      campaignSummary: 'campaignReports/getAccountSummary',
      labels: 'labels/getLabels',
    }),
  },
  mounted() {
    this.fetchLabels();
    this.fetchData();
  },
  methods: {
    async fetchLabels() {
      try {
        await this.$store.dispatch('labels/get');
      } catch (error) {
        console.error('Failed to fetch labels:', error);
      }
    },
    onDateRangeChange() {
      // Auto-apply when date range changes
      this.fetchData();
    },
    onLabelChange() {
      // Auto-apply when label changes
      this.fetchData();
    },
    getDateRange() {
      const now = new Date();
      const days = parseInt(this.selectedDateRange, 10);
      const fromDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      
      return {
        from: Math.floor(fromDate.getTime() / 1000),
        to: Math.floor(now.getTime() / 1000),
      };
    },
    async fetchData() {
      try {
        const dateRange = this.getDateRange();
        const params = {
          ...dateRange,
        };

        // Add label filter if selected
        if (this.selectedLabel) {
          params.selected_label = this.selectedLabel;
        }
        
        await this.$store.dispatch('campaignReports/fetchAccountSummary', params);
        
        await this.$store.dispatch('campaignReports/fetchAccountReport', {
          ...params,
          metric: 'sent_count,delivered_count,read_count,failed_count',
          group_by: 'day',
        });
      } catch (error) {
        console.error('Failed to fetch campaign data:', error);
      }
    },
    formatDate(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleDateString();
    },
  },
};
</script>